
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lesson, lessons } from '@/data/lessons';
import { useUser } from '@/context/UserContext';
import { Lock, CheckCircle2, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface JourneyGridProps {
  lessons?: Lesson[];
  totalDays?: number;
}

const JourneyGrid: React.FC<JourneyGridProps> = ({ lessons: propLessons, totalDays }) => {
  const navigate = useNavigate();
  const { userProgress } = useUser();
  
  // If totalDays is provided, generate lessons array from it
  // Otherwise use the provided lessons prop
  const displayLessons = propLessons || (totalDays ? lessons.slice(0, totalDays) : []);
  
  const goToLesson = (day: number) => {
    if (day <= userProgress.currentDay) {
      navigate(`/lesson/${day}`);
    }
  };
  
  // Function to generate grid columns based on screen size
  const getColsClass = () => {
    return 'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5';
  };
  
  return (
    <div className={`grid ${getColsClass()} gap-4`}>
      {displayLessons.map((lesson) => {
        const isCompleted = userProgress.completedDays.includes(lesson.day);
        const isLocked = lesson.day > userProgress.currentDay;
        
        return (
          <div
            key={lesson.day}
            onClick={() => !isLocked && goToLesson(lesson.day)}
            className={cn(
              "hormony-card p-3 flex flex-col h-full",
              !isLocked && "cursor-pointer hover:shadow-md hover:translate-y-[-2px]",
              isLocked && "opacity-70"
            )}
          >
            <div className="flex justify-between items-start mb-2">
              <div 
                className={cn(
                  "day-indicator text-xs",
                  isCompleted ? "bg-[#91adaf] text-white" : 
                  lesson.day === userProgress.currentDay ? "bg-[#e27d5f] text-white" : 
                  "bg-gray-200 text-gray-700"
                )}
              >
                <span className="text-xs">Day</span>
                <span className="text-lg">{lesson.day}</span>
              </div>
              
              <div>
                {isCompleted && (
                  <CheckCircle2 className="text-[#91adaf]" size={20} />
                )}
                {isLocked && (
                  <Lock className="text-gray-400" size={18} />
                )}
              </div>
            </div>
            
            <h3 className="font-medium text-sm mb-1 line-clamp-2">{lesson.title}</h3>
            <p className="text-xs text-gray-600 mb-2 line-clamp-2">{lesson.subtitle}</p>
            
            <div className="mt-auto flex justify-between items-center pt-2 text-xs">
              <span className={cn(
                "px-2 py-1 rounded-full",
                isCompleted ? "bg-[#91adaf]/20 text-[#91adaf]" : 
                isLocked ? "bg-gray-200 text-gray-500" : 
                "bg-[#e27d5f]/20 text-[#e27d5f]"
              )}>
                {isCompleted ? 'Completed' : isLocked ? 'Locked' : 'Available'}
              </span>
              {!isLocked && (
                <ChevronRight size={16} className={isCompleted ? "text-[#91adaf]" : "text-[#e27d5f]"} />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default JourneyGrid;
